import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';

export type SummaryCardTint = 'purple' | 'green' | 'blue';

const tintStyles = (tint: SummaryCardTint) => (theme: Theme) => {
    const color = tint === 'purple' ? theme.palette.primary.main : tint === 'green' ? theme.palette.success.main : theme.palette.info.main;

    return { color, bgcolor: alpha(color, 0.12) };
};

interface SummaryCardProps {
    label: string;
    value: React.ReactNode;
    caption: string;
    icon: React.ReactNode;
    tint: SummaryCardTint;
    /** Fuller explanation of what this metric means, shown on hover over the label. */
    tooltip?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, caption, icon, tint, tooltip }) => (
    <Paper
        elevation={0}
        sx={{
            p: 1,
            flex: 1,
            minWidth: 200,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '14px',
            boxShadow: '0 1px 2px rgba(16, 22, 51, 0.04), 0 6px 20px rgba(16, 22, 51, 0.05)',
        }}
    >
        <Box
            sx={[
                { width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
                tintStyles(tint),
            ]}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            {tooltip ? (
                <Tooltip title={tooltip} arrow placement="top">
                    <Typography variant="body2" color="text.secondary" sx={{ cursor: 'help', width: 'fit-content' }}>
                        {label}
                    </Typography>
                </Tooltip>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            )}
            <Typography variant="h4" sx={{ my: 0.25, lineHeight: 1.2, fontWeight: 600 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {caption}
            </Typography>
        </Box>
    </Paper>
);

export default SummaryCard;
